/**
 * Top-level React error boundary. Catches any uncaught render-cycle
 * exception, reports it to the backend telemetry endpoint (→ Slack),
 * and renders a minimal fallback UI so the user sees something other
 * than a blank white page.
 *
 * Reporting is fire-and-forget — never blocks the fallback render.
 *
 * Not a substitute for try/catch around async operations. Those land
 * in window.unhandledrejection (wired separately in index.tsx).
 */

import React from 'react';
import { reportFrontendError } from '../api/apiClient';

interface State {
  hasError: boolean;
  message:  string;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || 'Unknown error' };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Fire-and-forget — reportFrontendError swallows its own errors.
    reportFrontendError(error?.message || 'React error', {
      stack:  (error?.stack || '') + '\n--- componentStack ---' + (errorInfo?.componentStack || ''),
      source: 'react.errorboundary',
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, sans-serif',
          padding: '2rem',
          color: '#333',
        }}>
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.5rem', margin: '0 0 1rem' }}>Something went wrong.</h1>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              The CIMA team has been notified. Refresh the page to try again, or
              return to the dashboard.
            </p>
            <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#999', marginBottom: '1.5rem' }}>
              {this.state.message}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1.25rem',
                background: '#7e4ea3',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
